import { Button, FormControl, FormHelperText, InputLabel, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { Formik } from "formik";
import { isEmpty } from "lodash-es";
import moment, { Moment } from "moment";
import { forwardRef, useState } from "react";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import { AppDateTimePicker } from "@/common/components/Datetime/AppDateTimePicker";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useEntityInputFormInfo } from "@/common/hooks/entity/useEntityInputFormInfo";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useCurrentCurrency } from "@/common/hooks/useCurrentCurrency";
import { useUserAffiliation } from "@/common/hooks/user/useUserAffiliation";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  CreateWheelOperationDto,
  EntityType,
  GeneralStageUpdateDtoOfWheelOperationStage,
  UpdateWheelOperationDto,
  VehicleDto,
  WheelOperationDto,
  WheelOperationScheduleType,
  WheelOperationStage,
} from "@/core/api/generated/v1";

import AppSplitDropdownButton from "../../Button/AppSplitDropdownButton";
import ApiEnumSelect from "../../Enum/ApiEnumSelect";
import ApiEnumsAutocomplete from "../../Enum/ApiEnumsAutocomplete";
import GeneralValidationError from "../../Error/GeneralValidationError";
import FileUploader from "../../Files/FileUploader";
import FormActions from "../../Form/FormActions";
import AppTextArea from "../../Form/Input/AppTextArea";
import FuelLevelInput from "../../Form/Input/FuelLevelInput";
import MileageInput from "../../Form/Input/MileageInput";
import PriceSummaryInput from "../../PriceSummary/PriceSummaryInput";
import EntityAffiliationInputV2 from "../EntityAffiliation/EntityAffiliationInputV2";
import InferEntityAffiliationFromVehicleFormikComputedField from "../EntityAffiliation/InferEntityAffiliationFromVehicleFormikComputedField";
import GeneralAttachedTagsInput from "../General/GeneralTag/GeneralAttachedTagsInput";
import VehicleAutocompleteOrCreate from "../Vehicle/VehicleAutocompleteOrCreate";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
  FormWrapperHandle,
} from "../components/BaseEntityCreateUpdate";
import { BaseEntityCreateUpdateExtras } from "../components/BaseEntityCreateUpdateExtras";

type DefaultValues = {
  vehicleId?: CreateWheelOperationDto["vehicleId"];
  supplierId?: CreateWheelOperationDto["supplierId"];
  departmentIds?: CreateWheelOperationDto["departmentIds"];
  locationIds?: CreateWheelOperationDto["locationIds"];
  tenantRequestsMeta?: CreateWheelOperationDto["tenantRequestsMeta"];
  scheduleType?: WheelOperationScheduleType;
  scheduledAt?: CreateWheelOperationDto["scheduledAt"];
  currency?: CreateWheelOperationDto["currency"];
};

export type WheelOperationCreateUpdateHandle = FormWrapperHandle;

export interface WheelOperationCreateUpdateProps extends BaseEntityCreateUpdateInheritableProps<
  WheelOperationDto,
  DefaultValues
> {
  wheelOperationId?: string;
}

export default forwardRef<WheelOperationCreateUpdateHandle, WheelOperationCreateUpdateProps>(
  function WheelOperationCreateUpdate(
    {
      wheelOperationId,
      defaultValues,
      onCreate,
      onUpdate,
      onSave,
      onCancel,
    }: WheelOperationCreateUpdateProps,
    ref,
  ) {
    const formInfo = useEntityInputFormInfo({ entityId: wheelOperationId });

    const mounted = useMounted();
    const { enqueueSnackbar } = useAppSnackbar();
    const currentCurrency = useCurrentCurrency();
    const userAffiliation = useUserAffiliation();

    const isCreate = !wheelOperationId;
    // const isEdit = !!wheelOperationId;

    const [isAttachmentFilesUploading, setIsAttachmentFilesUploading] = useState(false);

    const wheelOperationRequest = useApiRequest(
      apiClient.wheelOperationsApi.apiV1WheelOperationsWheelOperationIdGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        wheelOperationId: wheelOperationId!,
      },
      {
        deps: [wheelOperationId],
        skip: !wheelOperationId,
      },
    );
    const wheelOperation = wheelOperationRequest?.data;

    useBreadcrumbReplacements({
      waitTimeout: 10_000,
      idBreadcrumb: wheelOperation && {
        idValue: wheelOperation.id!,
        newTitle: wheelOperation.localNumber || "",
      },
    });

    const vehicleIdComputed = defaultValues?.vehicleId || wheelOperation?.vehicle?.id;

    return (
      <BaseEntityCreateUpdate
        entityType={EntityType.WheelOperation}
        entityId={wheelOperationId}
        entity={wheelOperation}
        entityRequest={wheelOperationRequest}
        isIniting={wheelOperationRequest.isLoading || !currentCurrency || userAffiliation.isIniting}
      >
        <Formik<
          BaseFormikValues &
            CreateWheelOperationDto &
            UpdateWheelOperationDto & {
              vehicle?: VehicleDto;
              supplier?: WheelOperationDto["supplier"];
              initialAttachments: WheelOperationDto["attachments"];
              total?: number;
            }
        >
          initialValues={{
            vehicleId: wheelOperation?.vehicle?.id || defaultValues?.vehicleId || undefined,
            supplierId: wheelOperation?.supplier?.id || defaultValues?.supplierId || undefined,
            departmentIds:
              (!isEmpty(defaultValues?.departmentIds) ? defaultValues!.departmentIds : undefined) ||
              (!isEmpty(wheelOperation?.departmentIds)
                ? wheelOperation!.departmentIds
                : undefined) ||
              (!isEmpty(userAffiliation.departments)
                ? userAffiliation.departments!.map((d) => d.id!)
                : undefined),
            locationIds:
              (!isEmpty(defaultValues?.locationIds) ? defaultValues!.locationIds : undefined) ||
              (!isEmpty(wheelOperation?.locationIds) ? wheelOperation!.locationIds : undefined) ||
              (!isEmpty(userAffiliation.locations)
                ? userAffiliation.locations!.map((l) => l.id!)
                : undefined),
            externalNumber: wheelOperation?.externalNumber || undefined,
            responsibleUser: {
              isCurrentUser: true,
              ...(wheelOperation?.responsibleUser || {}),
            },
            scheduledAt: wheelOperation?.scheduledAt || defaultValues?.scheduledAt || undefined,
            scheduleType:
              wheelOperation?.scheduleType ||
              defaultValues?.scheduleType ||
              WheelOperationScheduleType.Unscheduled ||
              undefined,
            serviceTypes: wheelOperation?.serviceTypes || undefined,
            mileage: wheelOperation?.mileage || undefined,
            fuelLevel: wheelOperation?.fuelLevel ?? undefined,
            notes: wheelOperation?.notes || "",
            currency:
              defaultValues?.currency || wheelOperation?.currency || currentCurrency || undefined,
            price: wheelOperation?.price || 0,
            discount: wheelOperation?.discount || undefined,
            tax: wheelOperation?.tax || undefined,
            initialAttachments: wheelOperation?.attachments || undefined,
            attachments: !isEmpty(wheelOperation?.attachments)
              ? wheelOperation?.attachments
              : undefined,
            tenantRequestsMeta:
              wheelOperation?.tenantRequestsMeta || defaultValues?.tenantRequestsMeta || undefined,
            tags: wheelOperation?.tags || undefined,
            newStage: undefined,
            submit: "",
          }}
          validationSchema={Yup.object().shape({
            // contractId: Yup.string().required("Contract is required"),
            // vehicleId: Yup.string().required("Vehicle is required"),
            // type: Yup.string().required("Type is required"),
          })}
          onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
            const values2: typeof values = {
              ...values,
            };

            try {
              if (isCreate) {
                const response = await apiClient.wheelOperationsApi.apiV1WheelOperationsPost({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  createWheelOperationDto: { ...values2 },
                });
                enqueueSnackbar("Wheel operation created.", { variant: "success" });
                onCreate && (await onCreate(response.data));
                onSave && (await onSave(response.data));
              } else {
                const response =
                  await apiClient.wheelOperationsApi.apiV1WheelOperationsWheelOperationIdPut({
                    nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                    wheelOperationId,
                    updateWheelOperationDto: {
                      ...values2,
                    },
                  });
                enqueueSnackbar("Wheel operation updated.", { variant: "success" });
                onUpdate && (await onUpdate(response.data));
                onSave && (await onSave(response.data));
              }

              if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
              }
            } catch (err: any) {
              if (mounted.current) {
                ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                setStatus({ success: false });
                setSubmitting(false);
              }
            }
          }}
        >
          {(formikProps) => {
            const {
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
              submitForm,
            } = formikProps;

            return (
              <form noValidate onSubmit={handleSubmit}>
                <BaseEntityCreateUpdateExtras
                  defaultImperativeHandle={{
                    formWrapperRef: ref,
                    formikProps: formikProps,
                  }}
                  guardProps={{
                    formikProps: formikProps,
                  }}
                />

                <InferEntityAffiliationFromVehicleFormikComputedField
                  formInfo={formInfo}
                  values={{
                    vehicleId: values.vehicleId,
                    vehicle: values.vehicle,
                    departmentIds: values.departmentIds,
                    locationIds: values.locationIds,
                  }}
                  onComputed={(computed) => {
                    setFieldValue("vehicle", computed.vehicle);
                    setFieldValue("departmentIds", computed.departmentIds);
                    setFieldValue("locationIds", computed.locationIds);
                  }}
                />

                <FormContentBlock spacing={2}>
                  <Box>
                    {/* Tags */}
                    <FormControl margin='dense' fullWidth>
                      <GeneralAttachedTagsInput
                        value={values.tags}
                        onChange={(newValue) => {
                          setFieldValue("tags", newValue);
                        }}
                      />
                      <FormHelperText error>
                        {ValidationHelper.getFormikErrorsAsString(errors.tags, {
                          isIncludeNested: false,
                        })}
                      </FormHelperText>
                    </FormControl>

                    {/* Vehicle */}
                    <FormControl margin='dense' fullWidth error={Boolean(errors.vehicleId)}>
                      <VehicleAutocompleteOrCreate
                        autocompleteProps={{
                          required: true,
                          disabled: !isCreate || !isEmpty(vehicleIdComputed),
                          entityId: values.vehicleId,
                          isPreload: isCreate,
                          onChange: (newValue) => {
                            setFieldValue(`vehicleId`, newValue?.id);
                            setFieldValue(`vehicle`, newValue);

                            setFieldValue(
                              "departmentIds",
                              values.vehicleId !== newValue?.id
                                ? undefined
                                : ArrayHelper.undefinedIfEmpty(values.departmentIds),
                            );
                            setFieldValue(
                              "locationIds",
                              values.vehicleId !== newValue?.id
                                ? undefined
                                : ArrayHelper.undefinedIfEmpty(values.locationIds),
                            );
                          },
                        }}
                        createFormPlacement='modal'
                        onCreate={(newValue) => {
                          setFieldValue(`vehicleId`, newValue?.id);
                          setFieldValue(`vehicle`, newValue);

                          setFieldValue(
                            "departmentIds",
                            values.vehicleId !== newValue?.id
                              ? undefined
                              : ArrayHelper.undefinedIfEmpty(values.departmentIds),
                          );
                          setFieldValue(
                            "locationIds",
                            values.vehicleId !== newValue?.id
                              ? undefined
                              : ArrayHelper.undefinedIfEmpty(values.locationIds),
                          );
                        }}
                      />
                      <FormHelperText>{errors.vehicleId}</FormHelperText>
                    </FormControl>

                    {/* Entity affiliation */}
                    <EntityAffiliationInputV2
                      departments={{
                        departmentIds: values.departmentIds,
                        onChange: (departmentIds) => {
                          setFieldValue("departmentIds", departmentIds);
                        },
                        error: errors.departmentIds,
                      }}
                      locations={{
                        locationIds: values.locationIds,
                        onChange: (locationIds) => {
                          setFieldValue("locationIds", locationIds);
                        },
                        error: errors.locationIds,
                      }}
                    />

                    {/* Schedule Type */}
                    <FormControl
                      fullWidth
                      margin='dense'
                      error={Boolean(touched.scheduleType && errors.scheduleType)}
                    >
                      <InputLabel required>Schedule type</InputLabel>
                      <ApiEnumSelect
                        type='WheelOperationScheduleType'
                        value={values.scheduleType}
                        onChange={(newValue) => {
                          setFieldValue("scheduleType", newValue);
                          setFieldValue("scheduledAt", undefined);
                        }}
                        selectProps={{
                          label: "Schedule type",
                          required: true,
                        }}
                      />
                      <FormHelperText>{touched.scheduleType && errors.scheduleType}</FormHelperText>
                    </FormControl>
                    {/* Schedule At */}
                    {values.scheduleType === WheelOperationScheduleType.Scheduled && (
                      <FormControl
                        margin='dense'
                        fullWidth
                        error={Boolean(touched.scheduledAt && errors.scheduledAt)}
                      >
                        <AppDateTimePicker
                          label='Schedule At'
                          value={(values.scheduledAt && moment(values.scheduledAt)) || null}
                          format={DATETIME_FORMATS.DISPLAY_DATETIME}
                          onChange={(newValue: Moment | null) => {
                            setFieldValue("scheduledAt", newValue?.format() || null);
                          }}
                        />
                        <FormHelperText>{touched.scheduledAt && errors.scheduledAt}</FormHelperText>
                      </FormControl>
                    )}

                    {/* Service Types */}
                    <FormControl
                      fullWidth
                      margin='dense'
                      error={Boolean(touched.scheduleType && errors.scheduleType)}
                    >
                      <ApiEnumsAutocomplete
                        required
                        label='Service types'
                        type='WheelServiceType'
                        values={values.serviceTypes}
                        onChange={(newValue) => setFieldValue("serviceTypes", newValue)}
                        textFieldProps={{
                          error: Boolean(errors.serviceTypes),
                        }}
                      />
                      <FormHelperText>{touched.scheduleType && errors.scheduleType}</FormHelperText>
                    </FormControl>

                    {/* Mileage */}
                    <MileageInput
                      error={Boolean(touched.mileage && errors.mileage)}
                      helperText={touched.mileage && errors.mileage}
                      vehicle={values.vehicle}
                      disabled={!values.vehicle || !isCreate}
                      fullWidth
                      name='mileage'
                      onBlur={handleBlur}
                      onChange={(newValue) => {
                        setFieldValue("mileage", newValue);
                      }}
                      value={values.mileage}
                    />

                    {/* Fuel level */}
                    <FormControl
                      margin='dense'
                      fullWidth
                      error={Boolean(touched.fuelLevel && errors.fuelLevel)}
                      sx={{ mb: 4 }}
                    >
                      <Typography component='div' gutterBottom>
                        Fuel level
                      </Typography>

                      <FuelLevelInput
                        onBlur={handleBlur}
                        onChange={(e, val) => setFieldValue("fuelLevel", val ?? undefined)}
                        value={values.fuelLevel ?? undefined}
                      />
                      <FormHelperText>{touched.fuelLevel && errors.fuelLevel}</FormHelperText>
                    </FormControl>
                    {/* Cost */}
                    <FormControl fullWidth margin='dense'>
                      <PriceSummaryInput
                        values={{
                          currency: values.currency,
                          subTotal: values.price,
                          discount: values.discount,
                          tax: values.tax,
                          total: values.total,
                        }}
                        formikProps={{
                          errors: {
                            subTotal: errors.price,
                            discount: errors.discount,
                            tax: errors.tax,
                            total: errors.total,
                          },
                        }}
                        displayProps={{
                          insurance: false,
                          calcExplanationInline: false,
                          calcExplanationModal: true,
                        }}
                        inputsProps={{
                          all: {
                            margin: "none",
                          },
                          subTotal: {
                            label: "Price",
                            required: true,
                          },
                          total: {
                            required: true,
                          },
                        }}
                        onChange={(newValue) => {
                          setFieldValue(`currency`, newValue?.currency);
                          setFieldValue(`price`, newValue?.subTotal);
                          setFieldValue(`discount`, newValue?.discount);
                          setFieldValue(`tax`, newValue?.tax);
                          setFieldValue(`total`, newValue?.total);
                        }}
                      />
                    </FormControl>

                    <FormControl margin='dense' fullWidth>
                      <AppTextArea
                        error={Boolean(touched.notes && errors.notes)}
                        fullWidth
                        helperText={touched.notes && errors.notes}
                        mode='notes'
                        margin='dense'
                        name='notes'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.notes}
                        variant='outlined'
                      />
                    </FormControl>

                    <FormControl fullWidth component='fieldset' margin='dense'>
                      <FileUploader
                        multiple
                        defaultFiles={FileItem.createManyFrom(values.initialAttachments)}
                        onChange={(newFiles) => {
                          setFieldValue(
                            `attachments`,
                            FileItem.toManyGeneralAttachmentInputDto(newFiles),
                          );
                        }}
                        onUploadStarted={() => {
                          setIsAttachmentFilesUploading(true);
                        }}
                        onUploadFinished={() => {
                          setIsAttachmentFilesUploading(false);
                        }}
                      />
                    </FormControl>
                  </Box>
                </FormContentBlock>

                <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                <FormActions>
                  <AppSplitDropdownButton
                    isSelectAndTriggerAction={false}
                    buttonProps={{
                      variant: "contained",
                      color: "primary",
                      type: "submit",
                      fullWidth: true,
                      loading: isSubmitting,
                      disabled: isAttachmentFilesUploading,
                    }}
                    options={[
                      {
                        content: "Save",
                        onClick: () => {
                          submitForm();
                        },
                      },
                      {
                        content: "Save & Start",
                        if: isCreate,
                        onClick: () => {
                          const newValue: GeneralStageUpdateDtoOfWheelOperationStage = {
                            newStage: WheelOperationStage.InProgress,
                          };
                          setFieldValue("newStage", newValue);
                          submitForm();
                        },
                      },
                      {
                        content: "Save & Complete",
                        if: isCreate,
                        onClick: () => {
                          const newValue: GeneralStageUpdateDtoOfWheelOperationStage = {
                            newStage: WheelOperationStage.Completed,
                          };
                          setFieldValue("newStage", newValue);
                          submitForm();
                        },
                      },
                    ]}
                  />

                  {onCancel && (
                    <Button
                      variant='outlined'
                      color='text'
                      disabled={isSubmitting}
                      onClick={onCancel}
                    >
                      Cancel
                    </Button>
                  )}
                </FormActions>
              </form>
            );
          }}
        </Formik>
      </BaseEntityCreateUpdate>
    );
  },
);
